import { useMediaQuery } from '@abyss/web/hooks/useMediaQuery';
import { styled } from '@abyss/web/tools/styled';
import { Button } from '@abyss/web/ui/Button';
import { IconMaterial } from '@abyss/web/ui/IconMaterial';
import { Layout } from '@abyss/web/ui/Layout';
import { Popover } from '@abyss/web/ui/Popover';
import { RadioGroup } from '@abyss/web/ui/RadioGroup';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { adobeLinkTrackEvent } from '../AdobeTagging/adobeLinkTrackEvent';
import { Constants } from '../Constants';
import { notMobile, phoneOnly } from '../ConstantsStyles';
import {
  DesktopSortText,
  MobileText,
  radioGroupStyles,
  swapIconStyles,
} from '../ProviderDetailTabs/PhysicianDirectory/PhysicianDirectory.styled';
import { AllPatientReviewsSortMobile } from './AllPatientReviewsSortMobile';
import { PatientReviewsSortOrder } from './AllPatientReviewsSortOrder';

const getActiveElement = (el: HTMLElement): Element | null => {
  const root = el.getRootNode() as Document | ShadowRoot;
  return root.activeElement;
};

const forceFocus = (element: HTMLElement) => {
  const stop = (e: Event) => e.stopImmediatePropagation();
  document.addEventListener('focusin', stop, { capture: true, once: true });
  document.addEventListener('focusout', stop, { capture: true, once: true });
  element.focus();
  requestAnimationFrame(() => {
    document.removeEventListener('focusin', stop, { capture: true });
    document.removeEventListener('focusout', stop, { capture: true });
  });
};

const buildTabOrder = (
  container: HTMLElement,
  dialog: Element | null
): HTMLElement[] => {
  const tabOrder: HTMLElement[] = [];
  const checkedRadio =
    container.querySelector<HTMLInputElement>('input[type="radio"]:checked') ||
    container.querySelector<HTMLInputElement>('input[type="radio"]');
  if (checkedRadio) tabOrder.push(checkedRadio);

  const containerButtons = container.querySelectorAll<HTMLElement>('button');
  const sortBtn =
    containerButtons.length > 0
      ? containerButtons[containerButtons.length - 1]
      : null;
  if (sortBtn) tabOrder.push(sortBtn);

  if (dialog) {
    const dialogButtons = Array.from(
      dialog.querySelectorAll<HTMLElement>('button')
    ).filter((btn) => !container.contains(btn));
    if (dialogButtons.length > 0) tabOrder.push(dialogButtons[0]);
  }
  return tabOrder;
};

const handleTabNavigation = (
  e: KeyboardEvent,
  container: HTMLElement,
  dialog: Element | null,
  active: Element | null
) => {
  e.preventDefault();
  e.stopImmediatePropagation();
  const tabOrder = buildTabOrder(container, dialog);
  const idx = tabOrder.findIndex(
    (el) => el === active || el.contains(active as Node)
  );
  if (e.shiftKey) {
    if (idx <= 0) forceFocus(tabOrder[tabOrder.length - 1]);
    else forceFocus(tabOrder[idx - 1]);
  } else if (idx === -1 || idx >= tabOrder.length - 1) {
    forceFocus(tabOrder[0]);
  } else {
    forceFocus(tabOrder[idx + 1]);
  }
};

const focusInitialRadio = (container: HTMLElement) => {
  const radios = Array.from(
    container.querySelectorAll<HTMLInputElement>('input[type="radio"]')
  );
  const target = radios.find((r) => r.checked) || radios[0];
  forceFocus(target || container);
};

const handleRadioArrowNavigation = (
  e: KeyboardEvent,
  radios: HTMLInputElement[],
  focusedIndex: number,
  setTemporarySortOrder: (val: PatientReviewsSortOrder) => void
) => {
  e.preventDefault();
  e.stopImmediatePropagation();
  const isDown = e.key === 'ArrowDown';
  const nextIndex = isDown
    ? (focusedIndex + 1) % radios.length
    : (focusedIndex - 1 + radios.length) % radios.length;
  forceFocus(radios[nextIndex]);
  setTemporarySortOrder(radios[nextIndex].value as PatientReviewsSortOrder);
};

export const AllPatientReviewsSortingRadioGroup = ({
  setSortOrder,
  sortOrder,
  setIsOpen,
}: {
  setSortOrder: (val: PatientReviewsSortOrder) => void;
  sortOrder: PatientReviewsSortOrder;
  setIsOpen: (val: boolean) => void;
}) => {
  const { t } = useTranslation();
  const [temporarySortOrder, setTemporarySortOrder] =
    useState<PatientReviewsSortOrder>(sortOrder);
  const containerRef = useRef<HTMLDivElement>(null);

  const handlePopoverKeyDown = useCallback(
    (e: KeyboardEvent) => {
      const container = containerRef.current;
      if (!container) return;

      const dialog = container.closest('[role="dialog"]');
      const scope = (dialog || container) as HTMLElement;
      if (!scope.contains(e.target as Node)) return;

      const active = getActiveElement(container);

      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopImmediatePropagation();
        setIsOpen(false);
        return;
      }

      if (e.key === 'Tab') {
        handleTabNavigation(e, container, dialog, active);
        return;
      }

      const radios = Array.from(
        container.querySelectorAll<HTMLInputElement>('input[type="radio"]')
      );
      const focusedIndex = radios.findIndex((radio) => radio === active);

      if (focusedIndex === -1) {
        if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
          e.preventDefault();
          e.stopImmediatePropagation();
          const checkedRadio = radios.find((r) => r.checked) || radios[0];
          if (checkedRadio) forceFocus(checkedRadio);
        }
        return;
      }

      if (e.key === 'Enter') {
        e.preventDefault();
        e.stopImmediatePropagation();
        const focusedRadio = radios[focusedIndex];
        if (focusedRadio) {
          const selected = focusedRadio.value as PatientReviewsSortOrder;
          setTemporarySortOrder(selected);
          setSortOrder(selected);
          setIsOpen(false);
        }
        return;
      }

      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        handleRadioArrowNavigation(
          e,
          radios,
          focusedIndex,
          setTemporarySortOrder
        );
      }
    },
    [setSortOrder, setIsOpen]
  );

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;

    window.addEventListener('keydown', handlePopoverKeyDown, true);

    let raf2: number;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => focusInitialRadio(container));
    });

    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
      window.removeEventListener('keydown', handlePopoverKeyDown, true);
    };
  }, [handlePopoverKeyDown]);

  return (
    <div ref={containerRef} style={{ outline: 'none' }} tabIndex={-1}>
      <RadioGroup
        css={radioGroupStyles}
        data-auto-testid="all-patient-reviews-sort-order"
        data-testid="all-patient-reviews-sort-order"
        label={t('SORT_BUTTONS.SORT_REVIEWS')}
        onChange={(e: React.ChangeEvent<any>) => {
          setTemporarySortOrder(e.target.value);
          adobeLinkTrackEvent({
            name: `all-patient-reviews-sort-order ${e.target.value}`,
            location: `modal:all patient reviews sort`,
            type: 'internal',
            destinationUrl: '',
          });
        }}
        value={temporarySortOrder}
      >
        <RadioGroup.Radio
          css={{
            'abyss-radio-label': {
              color:
                temporarySortOrder === PatientReviewsSortOrder.MOST_RECENT
                  ? '#323334'
                  : '#4B4D4F',
              fontSize: '18px',
            },
            'abyss-radio-root': {
              alignItems: 'center',
              padding: '12px 0',
            },
          }}
          label={t(PatientReviewsSortOrder.MOST_RECENT)}
          value={PatientReviewsSortOrder.MOST_RECENT}
        />
        <RadioGroup.Radio
          css={{
            'abyss-radio-label': {
              color:
                temporarySortOrder === PatientReviewsSortOrder.HIGHEST_RATING
                  ? '#323334'
                  : '#4B4D4F',
              fontSize: '18px',
            },
            'abyss-radio-root': {
              padding: '12px 0',
              alignItems: 'center',
              marginTop: '0px !important',
            },
          }}
          label={t(PatientReviewsSortOrder.HIGHEST_RATING)}
          value={PatientReviewsSortOrder.HIGHEST_RATING}
        />
        <RadioGroup.Radio
          css={{
            'abyss-radio-label': {
              color:
                temporarySortOrder === PatientReviewsSortOrder.LOWEST_RATING
                  ? '#323334'
                  : '#4B4D4F',
              fontSize: '18px',
            },
            'abyss-radio-root': {
              padding: '12px 0',
              alignItems: 'center',
              marginTop: '0px !important',
            },
          }}
          label={t(PatientReviewsSortOrder.LOWEST_RATING)}
          value={PatientReviewsSortOrder.LOWEST_RATING}
        />
      </RadioGroup>
      <Button
        css={{
          marginTop: '24px',
          height: '42px',
          width: 'auto',
          fontWeight: 600,
        }}
        data-auto-testid="all-patient-reviews-sort-order"
        data-testid="all-patient-reviews-sort-order"
        onClick={() => {
          setSortOrder(temporarySortOrder);
          setIsOpen(false);
          adobeLinkTrackEvent({
            name: `all-patient-reviews-sort-order`,
            location: `modal:all patient reviews sort`,
            type: 'internal',
            destinationUrl: '',
          });
        }}
      >
        {t('Sort')}
      </Button>
    </div>
  );
};

export const StyledPopover = styled(Popover, {
  '&.abyss-popover-content-container': {
    padding: '8px',
    zIndex: '999 !important',
  },
  '&.abyss-popover-close-button': {
    marginTop: '20px',
    marginRight: '$md',
  },
  '&.abyss-popover-close-icon': {
    height: '24px',
    width: '24px',
  },
});

export const AllPatientReviewsSortDesktop = ({
  setSortOrder,
  sortOrder,
  setIsOpen,
  isOpen,
  handleOnClick,
}: {
  setSortOrder: (val: PatientReviewsSortOrder) => void;
  sortOrder: PatientReviewsSortOrder;
  setIsOpen: (val: boolean) => void;
  isOpen: boolean;
  handleOnClick: () => void;
}) => {
  const { t } = useTranslation();
  const desktopScreen = useMediaQuery(notMobile);
  const mobileScreen = useMediaQuery(phoneOnly);
  const popoverRef = useRef<HTMLDivElement>(null);
  const sortButtonRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    sortButtonRef.current?.focus();
    const handleClickOutsidePopover = (event) => {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(event.target) &&
        !event.target.closest('.abyss-popover-content')
      ) {
        setIsOpen(false);
      }
    };

    //check if the click was anywhere inside the sort UI (button or popover)
    const handleDocumentPointerDown = (e: PointerEvent) => {
      const target = e.target as HTMLElement;
      const isInsidePopover =
        target.closest('[data-radix-popper-content-wrapper]') !== null ||
        target.closest('[data-testid="sort-popover"]') !== null ||
        target.closest('[data-testid="all-patient-reviews-sort-order"]') !==
          null ||
        target.closest(
          '[data-auto-testid="all-patient-reviews-sort-order"]'
        ) !== null ||
        target.closest('[data-testid="all-patient-reviews-sort-button"]') !==
          null;

      if (isInsidePopover) {
        e.stopImmediatePropagation();
      }
    };

    document.addEventListener('mousedown', handleClickOutsidePopover);
    document.addEventListener('pointerdown', handleDocumentPointerDown, true);
    return () => {
      document.removeEventListener('mousedown', handleClickOutsidePopover);
      document.removeEventListener('pointerdown', handleDocumentPointerDown, true);
    };
  }, []);

  return (
    <div ref={popoverRef}>
      <StyledPopover
        align="center"
        content={
          <AllPatientReviewsSortingRadioGroup
            setIsOpen={setIsOpen}
            setSortOrder={setSortOrder}
            sortOrder={sortOrder}
          />
        }
        css={{
          zIndex: 9999,
        }}
        data-auto-testid="sort-popover"
        data-testid="sort-popover"
        hasCustomButtonTrigger
        onOpenAutoFocus={(event: Event) => {
          // Prevent Radix FocusScope's auto-focus which breaks in Shadow DOM.
          event.preventDefault();
        }}
        onOpenChange={() => {
          if (isOpen) {
            setIsOpen(false);
          } else {
            handleOnClick();
          }
        }}
        open={isOpen}
        position="bottom"
        title=""
        width="277px"
      >
        <Button
          css={{
            'abyss-button-root': {
              marginRight: '$md',
              backgroundColor: 'transparent',
              borderColor: '$gray4',
              height: '42px',
              width: 'auto',
              '@screen < $md': {
                border: '2px solid $primary1',
              },
              padding: '$sm $lg $sm $md',
              fontWeight: '$semibold',
              '& div': {
                '&:first-child': {
                  marginLeft: 0,
                },
                '&:last-child': {
                  marginLeft: 0,
                  '& > span': {
                    marginRight: '3px',
                    color: '#19191A',
                  },
                },
                '& svg': {
                  fill: mobileScreen ? '$primary1' : '#19191A !important',
                },
              },
            },
          }}
          data-auto-testid="all-patient-reviews-sort-button"
          data-testid="all-patient-reviews-sort-button"
          fontWeight={600}
          ref={sortButtonRef}
          size="$md"
          variant="outline"
        >
          <Layout.Group>
            <IconMaterial css={swapIconStyles} icon="swap_vert" />
            {!desktopScreen && (
              <MobileText color="$primary1">
                {Constants.SORT_BUTTONS.SORT}
              </MobileText>
            )}
            {desktopScreen && (
              <React.Fragment>
                <DesktopSortText>{t(sortOrder)}</DesktopSortText>
                <IconMaterial icon="expand_more" />
              </React.Fragment>
            )}
          </Layout.Group>
        </Button>
      </StyledPopover>
    </div>
  );
};

export const AllPatientReviewsSortPopover = ({
  setSortOrder,
  sortOrder,
  setIsOpen,
  isOpen,
  handleOnClick,
}: {
  setSortOrder: (val: PatientReviewsSortOrder) => void;
  sortOrder: PatientReviewsSortOrder;
  setIsOpen: (val: boolean) => void;
  isOpen: boolean;
  handleOnClick: () => void;
}) => {
  const mobileScreen = useMediaQuery(phoneOnly);

  return !mobileScreen ? (
    <AllPatientReviewsSortDesktop
      handleOnClick={handleOnClick}
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      setSortOrder={setSortOrder}
      sortOrder={sortOrder}
    />
  ) : (
    <AllPatientReviewsSortMobile
      isOpen={isOpen}
      onApply={setIsOpen}
      setIsOpen={setIsOpen}
      setSortOrder={setSortOrder}
      sortOrder={sortOrder}
    />
  );
};
